import {
  createTaskApi,
  getGenericEntityListingAPI,
  GetGenericEntityListingApiType,
  getWorkflowByEntityNameAndRecordId,
} from '@app-store'
import { FaxDetailsData } from '../../../types/intakeQueue.types'
import {
  DUPLICATE_POPUP,
  ENTITY_NAME,
  MANUAL_TASK_CONSTANTS,
  NAMESPACES,
  TASK_STATUS,
  TASK_TEMPLATE_CHANNEL_MAPPING,
  TASK_TYPE_TO_TEMPLATE_ID_MAP,
  WORKFLOW_OUTCOMES,
} from '../../../utils/constants'
import { toTitleCase } from '../../../utils/helperFunctions'
import { getInboundUiIdentifierForTemplate } from '../../../utils/workflowInbound.utils'

const formatTaskDate = (date: Date) =>
  date.toISOString().replace('.000Z', '+00:00')

const fetchDocumentMappingDataById = async (
  documentId?: number
): Promise<string> => {
  if (!documentId) {
    return ''
  }

  const payload = {
    entity_name: ENTITY_NAME?.DOCUMENT,
    select: [NAMESPACES?.DOCUMENT_KEY],
    filters: [
      {
        logical_operator: 'AND',
        criteria: [
          {
            values: [documentId],
            operation: 'EQUALS',
            field_name: 'id',
          },
        ],
      },
    ],
  }
  const response = await getGenericEntityListingAPI(
    payload as unknown as GetGenericEntityListingApiType
  )

  if (!response?.[0]) {
    return ''
  }

  return String(response[0]?.document_key ?? '')
}

export interface CreateIntakeAttachTaskParams {
  attachedCaseId: string | number
  faxDetails?: FaxDetailsData | null
  assigneeUserId?: number
}

export const createIntakeAttachTask = async ({
  attachedCaseId,
  faxDetails,
  assigneeUserId,
}: CreateIntakeAttachTaskParams) => {
  const workflowResponse = await getWorkflowByEntityNameAndRecordId({
    entityName: ENTITY_NAME.CASE_LOWER,
    recordId: String(attachedCaseId),
  })
  const processInstanceId = workflowResponse?.data?.workflow_instance?.id
  if (!processInstanceId) {
    return
  }

  const now = new Date()
  const startDate = new Date(now.getTime())
  const dueDate = new Date(now.getTime() + 1 * 24 * 60 * 60 * 1000)
  const completion_date = startDate.toISOString().split('T')[0]

  const channel =
    faxDetails?.category_communication_type?.label?.toUpperCase() as keyof typeof TASK_TEMPLATE_CHANNEL_MAPPING
  const taskTemplate = channel && TASK_TEMPLATE_CHANNEL_MAPPING[channel]
  const taskTemplateId = channel && TASK_TYPE_TO_TEMPLATE_ID_MAP[taskTemplate]
  const uiIdentifier = getInboundUiIdentifierForTemplate(taskTemplate)
  const documentId = await fetchDocumentMappingDataById(
    faxDetails?.document?.id
  )

  await createTaskApi({
    case_id: String(attachedCaseId),
    name: `Receive ${toTitleCase(channel ?? 'Fax')}`,
    description: '',
    due_date: formatTaskDate(dueDate),
    start_date: formatTaskDate(startDate),
    completion_date: completion_date,
    outcome: WORKFLOW_OUTCOMES.SUCCESSFUL,
    step_id: `receive_${(channel ?? 'fax').toLowerCase()}`,
    state: MANUAL_TASK_CONSTANTS.COMPLETED.value,
    standalone: true,
    cancellable: true,
    record_status: TASK_STATUS.ACTIVE,
    notes: DUPLICATE_POPUP.DOCUMENT_RECEIVED_NOTE,
    process_instance_id: String(processInstanceId),
    assignee: {
      id: Number(assigneeUserId),
    },
    task_template: {
      id: taskTemplateId,
      name: taskTemplate,
    },
    entity_type_name: '',
    entity_name: '',
    identifier: {
      document_id: documentId,
      source_intake_id: String(
        faxDetails?.communication_id ??
          faxDetails?.enrollment_id ??
          faxDetails?.fax_name ??
          ''
      ),
      taskNoteValues: {
        isTaskNoteSuggestionBox: true,
        isTaskNoteAddButton: false,
      },
      ui_identifier: uiIdentifier,
    },
  })
}
